import { createAsyncThunk, createEntityAdapter, createSlice, Dictionary } from '@reduxjs/toolkit';
import { CrmInvoiceResponse, DetailsInvoiceResponse, ReduxEntity, StripeBackendResponse } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { extractIds, normalizeItems } from 'Douleutaras/utilities/redux/normalizeItems';

const INVOICES_ENDPOINT = 'api/public/invoice';

const transactionsAdapter = createEntityAdapter<DetailsInvoiceResponse>();

interface GetInvoicesInterface {
  jobId: number;
  page: number;
}

export const getInvoicePaymentInfo = createAsyncThunk<CrmInvoiceResponse, { requestedObj: string; endpoint: string }>(
  'settings/invoice/paymentInfo/get',
  async ({ requestedObj, endpoint }, api) => {
    const response = await makeRequest(`api/public/${endpoint}/${requestedObj}/`, {
      method: 'GET'
    })(api);

    return response.data;
  }
);

export const getInvoicePaymentRequest = createAsyncThunk<StripeBackendResponse, Dictionary<string>>(
  'settings/invoice/paymentRequest/get',
  async ({ requestedObj, endpoint, next_url } = {}, api) => {
    const response = await makeRequest(`api/public/${endpoint}/${requestedObj}/payment_request/`, {
      method: 'GET',
      additionalGetParams: {
        next_url
      }
    })(api);

    return response.data;
  }
);

export const makeInvoicePayableOnline = createAsyncThunk(
  'settings/invoice/payableOnline/post',
  async ({ requestedObj, endpoint, invoice }: { requestedObj: string; endpoint: string; invoice: CrmInvoiceResponse }, api) => {
    const response = await makeRequest(`api/public/${endpoint}/${requestedObj}/payable_online/`, {
      method: 'POST',
      body: invoice
    })(api);

    return response.data;
  }
);

export const getInvoices = createAsyncThunk(
  'invoice/v2/get',
  async ({jobId, page}: GetInvoicesInterface , api) => {
    const response = await makeRequest(
      `${INVOICES_ENDPOINT}/${jobId}/jobs/`,
      {
        method: 'GET',
        additionalGetParams: {
          page
        }
      }
    )(api);

    const normalizedResults = normalizeItems(response.data.results);

    return { ...response.data, normalizedResults };
  }
);

export const invoicesSliceV2 = createSlice({
  name: 'invoicesItems',
  initialState: transactionsAdapter.getInitialState({
    totalTransactions: 0,
    currentPageItems: [],
    entities: {},
    error: false,
    loading: false
  } as ReduxEntity<DetailsInvoiceResponse> & { totalTransactions: number; currentPageItems: Array<number> }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getInvoices.fulfilled, (state, action) => {
      transactionsAdapter.setAll(state, action.payload.normalizedResults || {});
      state.totalTransactions = action.payload.count;
      state.currentPageItems = extractIds(action.payload.results);
      state.loading = false;
    });
    builder.addCase(getInvoices.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getInvoicePaymentInfo.pending, state => {
      state.loading = true;
    });
    builder.addCase(getInvoicePaymentInfo.fulfilled, state => {
      state.loading = false;
    });
    builder.addCase(makeInvoicePayableOnline.pending, state => {
      state.loading = true;
    });
    builder.addCase(makeInvoicePayableOnline.fulfilled, state => {
      state.loading = false;
    });
    builder.addCase(getInvoicePaymentRequest.pending, state => {
      state.loading = true;
    });
    builder.addCase(getInvoicePaymentRequest.fulfilled, state => {
      state.loading = false;
    });
  }
});
